import type { TFunction } from "i18next";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TPortfolioSortDir } from "./portfolioStorage";

export type TSortOrderOption = {
  label: string;
  dir: TPortfolioSortDir;
};

export type TSortFieldDef = {
  key: string;
  label: string;
};

const DEFAULT_ORDER = [
  { labelKey: "sort.orders.azAsc", dir: "asc" },
  { labelKey: "sort.orders.azDesc", dir: "desc" }
] as const;

// Per-field "Order" overrides. Default is A–Z / Z–A; add an entry keyed by the
// sort-field key (e.g. "status", "lastModified", or a `dim:<id>` dimension key)
// to give that field custom order labels. The first option is the field's default
// direction. The popover renders the first option with a ↓ arrow, the second with ↑.
const ORDER_OVERRIDES = {
  lastModified: [
    { labelKey: "sort.orders.newestFirst", dir: "desc" },
    { labelKey: "sort.orders.oldestFirst", dir: "asc" }
  ],
  status: [
    { labelKey: "sort.orders.completedFirst", dir: "asc" },
    { labelKey: "sort.orders.draftFirst", dir: "desc" }
  ]
} as const;

function orderDefs(sortFieldKey: string) {
  if (sortFieldKey in ORDER_OVERRIDES) return ORDER_OVERRIDES[sortFieldKey as keyof typeof ORDER_OVERRIDES];
  return DEFAULT_ORDER;
}

export function sortOrderOptions(
  t: TFunction<"projects">,
  sortFieldKey: string
): readonly [TSortOrderOption, TSortOrderOption] {
  const [first, second] = orderDefs(sortFieldKey);
  return [
    { label: t(first.labelKey), dir: first.dir },
    { label: t(second.labelKey), dir: second.dir }
  ];
}

export function defaultSortDir(sortFieldKey: string): TPortfolioSortDir {
  return orderDefs(sortFieldKey)[0].dir;
}

const SYSTEM_SORT_FIELDS = [
  { key: "lastModified", labelKey: "card.lastModified" },
  { key: "name", labelKey: "card.projectName" },
  { key: "createdBy", labelKey: "card.creator" },
  { key: "modifiedBy", labelKey: "card.modifiedBy" },
  { key: "status", labelKey: "card.status" }
] as const;

export function sortFields(t: TFunction<"projects">, dimensions: TOrganizationProjectDimension[]): TSortFieldDef[] {
  return [
    ...SYSTEM_SORT_FIELDS.map((f) => ({ key: f.key, label: t(f.labelKey) })),
    ...dimensions.map((d) => ({ key: `dim:${String(d.id)}`, label: d.name }))
  ];
}

export function sortFieldLabel(
  t: TFunction<"projects">,
  sortFieldKey: string,
  dimensions: TOrganizationProjectDimension[]
): string {
  return sortFields(t, dimensions).find((f) => f.key === sortFieldKey)?.label ?? t("sort.default");
}
